import { useEffect, useRef, useState } from 'react'
import Player from '@vimeo/player'
import { shouldAutoplay, vimeoIdFromUrl, PLAYER_OPTIONS } from '../lib/video'
import { countView, stagesReached } from '../lib/videoViews'
import { PlayIcon, PauseIcon, SoundOnIcon, SoundOffIcon, ExpandIcon } from './icons'

/**
 * The announcement player, with controls we draw ourselves.
 *
 * ── WHY NOT JUST USE VIMEO'S CONTROLS ───────────────────────────────────────
 *
 * Because they cannot be made bigger. Vimeo's chrome renders inside the iframe
 * at a size the embedding page cannot reach — no parameter enlarges it, and CSS
 * does not cross an iframe boundary. Its buttons land around 24px. The people
 * this hub is for are frequently outdoors, in gloves, on a tablet, in daylight.
 * WCAG 2.2 SC 2.5.8 asks for 24px minimum and 44px is the practical floor for a
 * gloved thumb, so every control below is 48px with a 4.5:1 contrast icon.
 *
 * ── WHY @vimeo/player AND NOT A CDN SCRIPT TAG ──────────────────────────────
 *
 * OPS-16: Wave 1 must not fetch its dependencies at runtime. The obvious way to
 * drive a Vimeo embed is <script src="https://player.vimeo.com/api/player.js">,
 * and that is exactly the runtime fetch OPS-16 forbids — it would also fail on
 * the first crew member to open the hub with one bar of signal, in a way that
 * looks like the video is broken rather than like a script did not load.
 *
 * The npm package is the same library, bundled by Vite at build time. It ships
 * in our own JS, is cached by the service worker with everything else, and the
 * player still works offline enough to say so.
 *
 * ── WHY A postMessage BRIDGE IS FINE HERE ───────────────────────────────────
 *
 * The SDK talks to the iframe over postMessage. That is a cross-origin channel,
 * so it is worth stating what crosses it: play, pause, volume, current time.
 * No identity, no session, nothing about the viewer. `dnt=1` on the embed keeps
 * Vimeo's own analytics cookies out of it. If that ever stops being true, this
 * component is the place it would show up.
 *
 * ── 198: THE PLAYER COUNTS ITS OWN VIEWS, AS TOTALS ─────────────────────────
 *
 * dnt=1 also switches off Vimeo's view stats, so `countAs` (the Vimeo id, or
 * undefined for "do not count") reports four stages — started, sound, half,
 * finished — through lib/videoViews.ts, once per device each. Nothing about
 * the viewer is sent: the server keeps a number per stage per day (rule 8).
 * Home passes it only once the card is shown to staff, so a scheduler
 * checking the video before the announcement does not count.
 *
 * ── AUTOPLAY IS MUTED OR IT IS NOTHING ──────────────────────────────────────
 *
 * Browsers block autoplay with sound and block it silently — play() rejects and
 * the viewer sees a frozen frame. So autoplay here always starts muted, and the
 * unmute control is deliberately the loudest thing on the player: a labelled
 * pill, not a speaker glyph, because "there is sound and you are not hearing
 * it" is information the viewer needs, not a control they need to discover.
 *
 * shouldAutoplay() decides whether to start at all — see lib/video.ts for the
 * three suppressions. Even when it says yes, play() can still reject (iOS low
 * power mode, a stricter setting than the API admits to). That is caught, and
 * the player falls back to a paused first frame with a large play button, which
 * is a fine outcome rather than an error.
 */
export function VideoPlayer({
  embedUrl,
  title,
  durationLabel,
  countAs,
}: {
  embedUrl: string
  title: string
  durationLabel?: string
  /** 198. The Vimeo id to count views under; absent = count nothing. */
  countAs?: string
}) {
  const holder = useRef<HTMLDivElement | null>(null)
  const player = useRef<Player | null>(null)
  // A ref, not an effect dependency: the gate can open while the video plays,
  // and re-creating the player for that would restart it.
  const countRef = useRef(countAs)
  countRef.current = countAs

  const [playing, setPlaying] = useState(false)
  const [muted, setMuted] = useState(true)
  const [volume, setVolume] = useState(1)
  const [progress, setProgress] = useState(0)
  const [duration, setDuration] = useState(0)
  const [ready, setReady] = useState(false)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!holder.current) return

    // ── PASS `id` PLUS OPTIONS, NEVER `url` ALONE ───────────────────────────
    //
    // This used to be `new Player(holder.current, { url: embedUrl })`, and the
    // symptom was Vimeo's own control bar visible inside ours — two players'
    // worth of buttons on one video.
    //
    // The SDK does not embed the URL you hand it. It parses out the video id
    // and builds a fresh iframe from the options object, discarding every query
    // parameter — so `controls=0` in the URL had no effect at all. Giving it the
    // bare id and the options explicitly is the form that works.
    //
    // The url fallback is for the case where the id cannot be parsed. Vimeo's
    // small controls are a worse player than ours; no player is worse still.
    const id = vimeoIdFromUrl(embedUrl)
    const p = new Player(
      holder.current,
      (id ? { id: Number(id), ...PLAYER_OPTIONS } : { url: embedUrl, ...PLAYER_OPTIONS }) as never,
    )
    player.current = p

    // The SDK builds the iframe, so these two attributes have to be set on the
    // element it made rather than written in JSX — and both are load-bearing.
    //
    //   referrerPolicy: Vimeo's domain privacy is a Referer check. netlify.toml
    //     sets strict-origin-when-cross-origin in production, but local dev has
    //     no Netlify and therefore no header, and without a referrer the embed
    //     refuses to play in a way that looks exactly like a deleted video. A
    //     video that only works in production is a video nobody tests.
    //   allow: muted autoplay is refused outright unless "autoplay" is in the
    //     iframe's permissions policy. The SDK sets a list of its own on newer
    //     versions and this makes it explicit rather than version-dependent.
    const frame = holder.current.querySelector('iframe')
    if (frame) {
      frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin')
      frame.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture')
      frame.setAttribute('title', title)
    }

    // Vimeo's own events, not a polling loop. A setInterval here would keep the
    // main thread and the radio awake on a phone for the whole video.
    // 198: each handler also counts its stage. countView decides whether this
    // device has counted it already, so calling it on every event is cheap.
    const count = (stage: Parameters<typeof countView>[1]) => {
      const id = countRef.current
      if (id) void countView(id, stage)
    }
    p.on('play', () => {
      setPlaying(true)
      count('started')
    })
    p.on('pause', () => setPlaying(false))
    p.on('ended', () => {
      setPlaying(false)
      count('finished')
    })
    p.on('timeupdate', (d: { seconds: number; duration: number }) => {
      setProgress(d.seconds)
      setDuration(d.duration)
      for (const stage of stagesReached(d.seconds, d.duration)) count(stage)
    })
    p.on('volumechange', (d: { volume: number }) => {
      setVolume(d.volume)
      setMuted(d.volume === 0)
      // The player sets volume 0 itself before autoplay; only a volume above
      // zero is the viewer choosing sound.
      if (d.volume > 0) count('sound')
    })

    p.ready()
      .then(async () => {
        setReady(true)
        // The real length, before anyone presses play. The clock used to show a
        // hand-typed durationLabel until the first timeupdate, and that label
        // was the OLD video's length the day the id changed (5 Oct). Asking the
        // SDK makes the label a fallback for a refused call, not a fact.
        p.getDuration().then((d) => { if (d > 0) setDuration(d) }).catch(() => {})
        // The frame takes the VIDEO'S shape, not a guess at it (5 Oct). Vimeo's
        // embed code for "Welcome to OneConetec" padded 75% (4:3) while its
        // upload was unfinished; the file that arrived is 3840x2160, 16:9. A
        // hand-typed ratio would have been wrong one way or the other, and a
        // wrong one is a letterbox or a pillarbox on the one video everybody
        // is sent to watch — so ask the player. The inline style lands on
        // .player__frame itself, so the ratio still lives on the frame and
        // never on the container (hub.css; the control bar must ADD height).
        // 16:9 stays as the stylesheet's default for the moment before this
        // answers, and for a refused call.
        Promise.all([p.getVideoWidth(), p.getVideoHeight()])
          .then(([w, h]) => {
            if (w > 0 && h > 0 && holder.current) holder.current.style.aspectRatio = `${w} / ${h}`
          })
          .catch(() => {})
        await p.setVolume(0) // muted, or autoplay is refused
        if (shouldAutoplay()) {
          try {
            await p.play()
          } catch {
            /* Refused. A paused first frame with a big play button is a fine
               outcome — do not surface this as an error, nothing is wrong. */
          }
        }
      })
      .catch(() => {
        // The embed itself failed. Almost always the Vimeo domain allow-list
        // (see lib/video.ts); occasionally the network. Either way the honest
        // thing is to say the video will not play here, not to show dead chrome.
        setFailed(true)
      })

    return () => {
      // Destroy on unmount, or the iframe keeps playing audio into a route the
      // viewer has already left.
      p.destroy().catch(() => {})
      player.current = null
    }
  }, [embedUrl, title])

  async function toggle() {
    const p = player.current
    if (!p) return
    if (playing) await p.pause()
    else await p.play().catch(() => {})
  }

  async function toggleMute() {
    const p = player.current
    if (!p) return
    // Unmuting restores whatever the viewer last chose rather than slamming to
    // full. Volume 0 IS mute for the SDK, so there is no separate mute flag to
    // get out of step with.
    await p.setVolume(muted ? 1 : 0)
  }

  async function seek(seconds: number) {
    const p = player.current
    if (!p) return
    await p.setCurrentTime(seconds)
    setProgress(seconds)
  }

  async function setLevel(level: number) {
    const p = player.current
    if (!p) return
    await p.setVolume(level)
  }

  if (failed) {
    return (
      <div className="player__failed">
        <p className="player__failedtitle">This video will not play here.</p>
        <p className="player__failedsub">
          The hub host has to be on the video&apos;s allow-list. Nothing is wrong
          with your connection.
        </p>
      </div>
    )
  }

  const pct = duration > 0 ? (progress / duration) * 100 : 0

  return (
    <div className="player">
      <div className="player__frame" ref={holder} />

      {/* The unmute prompt. Large, worded, and over the picture — a muted video
          playing silently reads as a broken video, and a speaker glyph in the
          corner is not enough to correct that impression. */}
      {ready && playing && muted && (
        <button className="player__unmute" onClick={toggleMute} type="button">
          <SoundOffIcon size={18} />
          Tap for sound
        </button>
      )}

      <div className="player__bar">
        <button
          className="player__btn"
          onClick={toggle}
          type="button"
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {playing ? <PauseIcon size={22} /> : <PlayIcon size={22} />}
        </button>

        {/* A range input, not a styled div. It is keyboard-operable, it is
            announced correctly, and it drags on touch — all of which a div with
            a click handler would have to reimplement badly. */}
        <input
          className="player__seek"
          type="range"
          min={0}
          max={Math.max(duration, 0.1)}
          step={0.5}
          value={progress}
          onChange={(e) => seek(Number(e.target.value))}
          aria-label="Seek"
          aria-valuetext={`${fmt(progress)} of ${fmt(duration)}`}
          style={{ ['--pct' as string]: `${pct}%` }}
        />

        <span className="player__time">
          {fmt(progress)} / {duration ? fmt(duration) : (durationLabel ?? '–:––')}
        </span>

        <button
          className="player__btn"
          onClick={toggleMute}
          type="button"
          aria-label={muted ? 'Unmute' : 'Mute'}
          aria-pressed={muted}
        >
          {muted ? <SoundOffIcon size={22} /> : <SoundOnIcon size={22} />}
        </button>

        {/* Hidden below the tablet breakpoint: a volume slider on a phone is
            two millimetres of travel, and the hardware buttons already do it. */}
        <input
          className="player__vol"
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={volume}
          onChange={(e) => setLevel(Number(e.target.value))}
          aria-label="Volume"
        />

        <button
          className="player__btn"
          onClick={() => player.current?.requestFullscreen().catch(() => {})}
          type="button"
          aria-label="Full screen"
        >
          <ExpandIcon size={22} />
        </button>
      </div>
    </div>
  )
}

function fmt(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}
